import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DayPilotMonth } from '@daypilot/daypilot-lite-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import TodayIcon from '@mui/icons-material/Today';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import EngineeringIcon from '@mui/icons-material/Engineering';
import * as chanceApi from '../../lib/api/chance';
import * as projectApi from '../../lib/api/project';
import * as taxApi from '../../lib/api/taxbill';
import * as authApi from '../../lib/api/auth';
import * as salesScheduleApi from '../../lib/api/salesSchedule';
import * as billCollectPlanApi from '../../lib/api/billCollectPlan';
import SalesScheduleFormDialog from './SalesScheduleFormDialog';
import TechEditStepperModalContainer from './TechEditStepperModalContainer';

// 기술지원(작업 지원 요청/예정) 일정의 칩·이벤트 색. 나머지 네 소스(영업기회/프로젝트/발행예정/
// 수금예정)와 구분되면서, 예전 기술지원 캘린더가 쓰던 호박색 계열을 유지한다.
const TECH_COLOR = '#f9a825';
const TECH_FONT_COLOR = '#212121';

// [수금예정] 칩 색 - 정상(예정)은 기존 초록, 계획 지연은 눈에 띄게 빨강으로 바꾼다
// (2026-10-21 2차 릴리스: "회차당 가장 이른 미완료 계획" 기준으로 지연 표시).
const COLLECTION_PLAN_COLOR = '#2e7d32';
const COLLECTION_PLAN_OVERDUE_COLOR = '#c62828';

const createSalesMockEvents = (month) => {
  const baseMonth = dayjs(month).startOf('month');

  return [
    {
      id: 'chance-mock-1',
      text: '[영업기회] 평택시청 네트워크 증설',
      start: baseMonth.date(4).format('YYYY-MM-DD'),
      end: baseMonth.date(5).format('YYYY-MM-DD'),
      backColor: '#1565c0',
      borderColor: '#1565c0',
      fontColor: '#ffffff',
      toolTip: '포캐스팅 단계: 제안',
      tags: {
        type: 'chance',
        link: '/chancemng',
      },
    },
    {
      id: 'chance-mock-2',
      text: '[영업기회] 관공서 보안장비 교체',
      start: baseMonth.date(11).format('YYYY-MM-DD'),
      end: baseMonth.date(12).format('YYYY-MM-DD'),
      backColor: '#00897b',
      borderColor: '#00897b',
      fontColor: '#ffffff',
      toolTip: '포캐스팅 단계: 계약 검토',
      tags: {
        type: 'chance',
        link: '/chancemng',
      },
    },
    {
      id: 'project-mock-1',
      text: '[프로젝트] SKV1타워 네트워크 구축',
      start: baseMonth.date(8).format('YYYY-MM-DD'),
      end: baseMonth.date(16).format('YYYY-MM-DD'),
      backColor: '#ef6c00',
      borderColor: '#ef6c00',
      fontColor: '#ffffff',
      toolTip: '수주 프로젝트 일정',
      tags: {
        type: 'project',
        link: '/projectmng',
      },
    },
    {
      id: 'project-mock-2',
      text: '[프로젝트] 시청 행정망 장비 구매',
      start: baseMonth.date(19).format('YYYY-MM-DD'),
      end: baseMonth.date(25).format('YYYY-MM-DD'),
      backColor: '#6d4c41',
      borderColor: '#6d4c41',
      fontColor: '#ffffff',
      toolTip: '계약부터 매출 예정까지',
      tags: {
        type: 'project',
        link: '/projectmng',
      },
    },
    {
      id: 'tax-mock-1',
      text: '[발행예정] 시청 행정망 장비 구매',
      start: baseMonth.date(21).format('YYYY-MM-DD'),
      end: baseMonth.date(22).format('YYYY-MM-DD'),
      backColor: '#8e24aa',
      borderColor: '#8e24aa',
      fontColor: '#ffffff',
      toolTip: '매출 세금계산서 발행 예정',
      tags: {
        type: 'taxIssuePlan',
        link: '/taxbillmng',
      },
    },
    {
      id: 'collect-mock-1',
      text: '[수금예정] SKV1타워 네트워크 구축',
      start: baseMonth.date(28).format('YYYY-MM-DD'),
      end: baseMonth.date(29).format('YYYY-MM-DD'),
      backColor: '#2e7d32',
      borderColor: '#2e7d32',
      fontColor: '#ffffff',
      toolTip: '매출 계산서 수금 예정',
      tags: {
        type: 'collectionPlan',
        link: '/taxbillmng',
      },
    },
    {
      id: 'tech-mock-1',
      text: '[기술지원] SKV1타워 작업 예정',
      start: baseMonth.date(15).format('YYYY-MM-DD'),
      end: baseMonth.date(16).format('YYYY-MM-DD'),
      backColor: TECH_COLOR,
      borderColor: TECH_COLOR,
      fontColor: TECH_FONT_COLOR,
      toolTip: '작업 지원 예정',
      tags: {
        type: 'tech',
        taskUuid: null,
      },
    },
  ];
};

const SalesScheduleContainer = () => {
  const navigate = useNavigate();
  const [monthStart, setMonthStart] = useState(dayjs().startOf('month'));
  const [events, setEvents] = useState([]);
  // 직접 등록한 일정을 넣고 고치는 팝업. null 이면 닫혀 있다.
  const [scheduleForm, setScheduleForm] = useState(null);
  // 기술지원 이벤트를 클릭하면 이 reqUuid로 기존 기술지원 상세(스테퍼) 팝업을 띄운다. null 이면 닫혀 있다.
  const [techReqId, setTechReqId] = useState(null);
  // 칩을 눌러서 숨긴 소스 종류. 다시 누르면 보인다.
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);

  const toggleType = useCallback((type) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    const rangeStart = monthStart.startOf('month').format('YYYY-MM-DD');
    const rangeEnd = monthStart.endOf('month').format('YYYY-MM-DD');

    try {
      const [
        chanceRes,
        projectRes,
        taxbillRes,
        collectPlanRes,
        myScheduleRes,
        techScheduleRes,
      ] = await Promise.allSettled([
        chanceApi.getBaseChances({
          useYn: 'Y',
          cstNm: '',
          strtDt: rangeStart,
          endDt: rangeEnd,
        }),
        projectApi.getProjects({
          cstNm: '',
          strtDt: rangeStart,
          endDt: rangeEnd,
        }),
        taxApi.getTaxbills({
          osTran: 'A',
          invTyp: '매출',
          searchTyp: '',
          reqCompYn: 'A',
          cstNm: '',
          strtDt: rangeStart,
          endDt: rangeEnd,
        }),
        // [수금예정] 칩의 진짜 소스(2026-10-21 2차 릴리스) - 회차당 PROC_PLAN_DT 단일값이
        // 아니라, 회차당 "가장 이른 미완료 계획"(PMS_BILL_COLLECT_PLAN) 하나씩.
        billCollectPlanApi.getCalendarPlans('매출', rangeStart, rangeEnd),
        salesScheduleApi.getSalesSchedules({
          startDate: rangeStart,
          endDate: rangeEnd,
        }),
        // 기술지원(작업 지원 예정) — 예전 기술지원 캘린더가 쓰던 것과 같은 API. 이 API는 날짜로
        // 거르지 않고 전체를 돌려주므로 월 범위는 아래에서 직접 걸러낸다.
        authApi.getSchedules(),
      ]);

      const chanceList =
        chanceRes.status === 'fulfilled' &&
        Array.isArray(chanceRes.value?.data?.list)
          ? chanceRes.value.data.list
          : [];
      const projectList =
        projectRes.status === 'fulfilled' &&
        Array.isArray(projectRes.value?.data?.list)
          ? projectRes.value.data.list
          : [];
      const taxbillList =
        taxbillRes.status === 'fulfilled' &&
        Array.isArray(taxbillRes.value?.data?.list)
          ? taxbillRes.value.data.list
        : [];

      const chanceEvents = chanceList
        .filter((item) => item.expDay || item.crtDt)
        .map((item) => {
          const start = dayjs(item.expDay || item.crtDt).format('YYYY-MM-DD');
          const title = item.vendorCmpNm
            ? `[영업기회] ${item.vendorCmpNm} · ${item.cstNm}`
            : `[영업기회] ${item.cstNm}`;

          return {
            id: `chance-${item.prjUuid}`,
            text: title,
            start,
            end: dayjs(start).add(1, 'day').format('YYYY-MM-DD'),
            backColor: '#1565c0',
            borderColor: '#1565c0',
            fontColor: '#ffffff',
            toolTip: `${item.cstStatus || '영업기회'} · ${item.crtNm || '담당자 미지정'}`,
            tags: {
              type: 'chance',
              link: item.prjUuid
                ? `/chancemng/${item.prjUuid}/${item.cstGrade || ''}/1`
                : '/chancemng',
            },
          };
        });

      const projectEvents = projectList
        .filter((item) => item.bookingDay || item.expDay)
        .map((item) => {
          const startDay = dayjs(item.bookingDay || item.expDay).format(
            'YYYY-MM-DD',
          );
          const endDay = item.expDay
            ? dayjs(item.expDay).add(1, 'day').format('YYYY-MM-DD')
            : dayjs(startDay).add(1, 'day').format('YYYY-MM-DD');

          return {
            id: `project-${item.prjUuid}`,
            text: `[프로젝트] ${item.cstNm}`,
            start: startDay,
            end: endDay,
            backColor: '#ef6c00',
            borderColor: '#ef6c00',
            fontColor: '#ffffff',
            toolTip: `${item.cmpStatus || '프로젝트'} · ${item.crtNm || '담당자 미지정'}`,
            tags: {
              type: 'project',
              link: item.prjUuid
                ? `/projectmng/${item.prjUuid}/${item.cmpGrade || ''}/1`
                : '/projectmng',
            },
          };
        });

      const taxIssuePlanEvents = taxbillList
        .filter((item) => item.reqPlanDt && item.reqCompYn !== 'Y')
        .map((item) => ({
          id: `tax-issue-plan-${item.invUuid}`,
          text: `[발행예정] ${item.cstNm}`,
          start: dayjs(item.reqPlanDt).format('YYYY-MM-DD'),
          end: dayjs(item.reqPlanDt).add(1, 'day').format('YYYY-MM-DD'),
          backColor: '#8e24aa',
          borderColor: '#8e24aa',
          fontColor: '#ffffff',
          toolTip: `${item.partnerNm || '-'} · ${
            item.invTot ? `${Number(item.invTot).toLocaleString()}원` : '금액 미정'
          }`,
          tags: {
            type: 'taxIssuePlan',
            link: item.invUuid ? `/taxbillmng/${item.invUuid}` : '/taxbillmng',
          },
        }));

      // [수금예정] - 회차당 PROC_PLAN_DT(단일값) 대신 "가장 이른 미완료 계획"
      // (PMS_BILL_COLLECT_PLAN) 하나씩. 서버(selectNextDuePlans)가 회차당 1건만 돌려주므로
      // 여기서는 날짜·색만 입힌다(2026-10-21 2차 릴리스).
      const collectPlanList =
        collectPlanRes.status === 'fulfilled' &&
        Array.isArray(collectPlanRes.value?.data?.list)
          ? collectPlanRes.value.data.list
          : [];

      const collectionPlanEvents = collectPlanList
        .filter((item) => item.planDt)
        .map((item) => {
          const overdue = item.status === '지연';
          const color = overdue ? COLLECTION_PLAN_OVERDUE_COLOR : COLLECTION_PLAN_COLOR;
          const remainTxt = item.remainAmt
            ? `${Number(item.remainAmt).toLocaleString()}원`
            : '금액 미정';
          const planNmTxt = item.planNm ? `(${item.planNm}) ` : '';

          return {
            id: `collection-plan-${item.invUuid}`,
            text: `[수금예정]${overdue ? '(지연)' : ''} ${planNmTxt}${item.cstNm || ''}`,
            start: dayjs(item.planDt).format('YYYY-MM-DD'),
            end: dayjs(item.planDt).add(1, 'day').format('YYYY-MM-DD'),
            backColor: color,
            borderColor: color,
            fontColor: '#ffffff',
            toolTip: `${item.partnerNm || '-'} · ${planNmTxt}잔액 ${remainTxt}${
              overdue ? ' · 지연' : ''
            }`,
            tags: {
              type: 'collectionPlan',
              link: item.invUuid ? `/taxbillmng/${item.invUuid}` : '/taxbillmng',
            },
          };
        });

      // 기술지원(작업 지원 예정). 예전 기술지원 캘린더(ScheduleContainer)와 대시보드 캘린더가
      // 쓰던 /api/v1/schedule/list 를 그대로 재사용한다 — 날짜로 거르지 않고 전체를 주므로
      // 여기서 이번 달 범위와 겹치는 것만 추린다.
      const rangeStartDay = dayjs(rangeStart);
      const rangeEndDay = dayjs(rangeEnd);
      const techScheduleList =
        techScheduleRes.status === 'fulfilled' &&
        Array.isArray(techScheduleRes.value?.data?.list)
          ? techScheduleRes.value.data.list
          : [];

      const techEvents = techScheduleList
        .filter((item) => item.type === 'tech' && item.start)
        .filter((item) => {
          const start = dayjs(item.start);
          const end = item.end ? dayjs(item.end) : start;
          return !end.isBefore(rangeStartDay, 'day') && !start.isAfter(rangeEndDay, 'day');
        })
        .map((item) => ({
          id: `tech-${item.taskUuid || item.id}`,
          text: `[기술지원] ${item.title}`,
          start: dayjs(item.start).format('YYYY-MM-DD'),
          end: dayjs(item.end || item.start).add(1, 'day').format('YYYY-MM-DD'),
          backColor: TECH_COLOR,
          borderColor: TECH_COLOR,
          fontColor: TECH_FONT_COLOR,
          toolTip: [item.usrNm, item.dptNm, item.gradeNm].filter(Boolean).join(' · ') || '작업 지원 예정',
          tags: {
            type: 'tech',
            taskUuid: item.taskUuid,
          },
        }));

      // 직접 등록한 일정. 위의 것들은 시스템이 만들어 주는 것이고 이건 사람이 적은 것이라
      // 색을 달리해서 구분한다.
      const myScheduleList =
        myScheduleRes.status === 'fulfilled' &&
        Array.isArray(myScheduleRes.value?.data?.list)
          ? myScheduleRes.value.data.list
          : [];

      const myScheduleEvents = myScheduleList.map((item) => {
        const start = dayjs(item.startDt).format('YYYY-MM-DD');
        const end = item.endDt
          ? dayjs(item.endDt).add(1, 'day').format('YYYY-MM-DD')
          : dayjs(start).add(1, 'day').format('YYYY-MM-DD');

        return {
          id: `schedule-${item.schdUuid}`,
          text: item.shareYn === 'Y' ? `[공유] ${item.title}` : item.title,
          start,
          end,
          backColor: '#00695c',
          borderColor: '#00695c',
          fontColor: '#ffffff',
          toolTip: [item.content, item.crtNm].filter(Boolean).join(' · '),
          tags: { type: 'mySchedule', schedule: item },
        };
      });

      const mergedEvents = [
        ...chanceEvents,
        ...projectEvents,
        ...taxIssuePlanEvents,
        ...collectionPlanEvents,
        ...techEvents,
        ...myScheduleEvents,
      ];

      if (mergedEvents.length === 0) {
        setUsingMock(true);
        setEvents(createSalesMockEvents(monthStart));
        setError('조회 결과가 없어 예시 일정을 표시합니다.');
      } else {
        setUsingMock(false);
        setEvents(mergedEvents);
      }
    } catch (err) {
      setUsingMock(true);
      setEvents(createSalesMockEvents(monthStart));
      setError(
        err?.response?.data?.message ||
          '영업 일정 데이터를 불러오지 못해 예시 일정을 표시합니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [monthStart]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const summary = useMemo(() => {
    const chanceCount = events.filter(
      (event) => event.tags?.type === 'chance',
    ).length;
    const projectCount = events.filter(
      (event) => event.tags?.type === 'project',
    ).length;
    const taxIssuePlanCount = events.filter(
      (event) => event.tags?.type === 'taxIssuePlan',
    ).length;
    const collectionPlanCount = events.filter(
      (event) => event.tags?.type === 'collectionPlan',
    ).length;
    const techCount = events.filter(
      (event) => event.tags?.type === 'tech',
    ).length;

    return {
      total: events.length,
      chanceCount,
      projectCount,
      taxIssuePlanCount,
      collectionPlanCount,
      techCount,
    };
  }, [events]);

  // 칩을 눌러 숨긴 소스는 달력에서도 뺀다. 직접 등록한 일정(mySchedule)은 칩이 없어 항상 보인다.
  const visibleEvents = useMemo(
    () => events.filter((event) => !hiddenTypes.has(event.tags?.type)),
    [events, hiddenTypes],
  );

  return (
    <Box>
      <Paper
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              일정 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              영업·프로젝트·세금계산서·기술지원 일정을 월간으로 모아 봅니다.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<TrendingUpIcon />}
              label={`영업기회 ${summary.chanceCount}`}
              color="primary"
              variant="outlined"
              onClick={() => toggleType('chance')}
              sx={{ opacity: hiddenTypes.has('chance') ? 0.4 : 1 }}
            />
            <Chip
              icon={<HandshakeIcon />}
              label={`프로젝트 ${summary.projectCount}`}
              color="warning"
              variant="outlined"
              onClick={() => toggleType('project')}
              sx={{ opacity: hiddenTypes.has('project') ? 0.4 : 1 }}
            />
            <Chip
              icon={<ReceiptLongIcon />}
              label={`발행예정 ${summary.taxIssuePlanCount}`}
              variant="outlined"
              onClick={() => toggleType('taxIssuePlan')}
              sx={{ borderColor: '#8e24aa', color: '#8e24aa', opacity: hiddenTypes.has('taxIssuePlan') ? 0.4 : 1 }}
            />
            <Chip
              icon={<PaymentsIcon />}
              label={`수금예정 ${summary.collectionPlanCount}`}
              variant="outlined"
              onClick={() => toggleType('collectionPlan')}
              sx={{ borderColor: '#2e7d32', color: '#2e7d32', opacity: hiddenTypes.has('collectionPlan') ? 0.4 : 1 }}
            />
            <Chip
              icon={<EngineeringIcon />}
              label={`기술지원 ${summary.techCount}`}
              variant="outlined"
              onClick={() => toggleType('tech')}
              sx={{ borderColor: TECH_COLOR, color: '#8d6e00', opacity: hiddenTypes.has('tech') ? 0.4 : 1 }}
            />
            <Chip label={`총 ${summary.total}건`} />
            {usingMock && <Chip label="예시 데이터" color="info" />}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{ mt: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => setMonthStart((prev) => prev.subtract(1, 'month'))}
            >
              이전달
            </Button>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={() => setMonthStart(dayjs().startOf('month'))}
            >
              이번달
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIosIcon />}
              onClick={() => setMonthStart((prev) => prev.add(1, 'month'))}
            >
              다음달
            </Button>
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {monthStart.format('YYYY년 MM월')}
          </Typography>

          {/* 직접 등록. 나머지 일정은 시스템이 만들어 주는 것이고 이건 사람이 적는 것이다. */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setScheduleForm({})}
          >
            일정 등록
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity={usingMock ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          minHeight: 720,
        }}
      >
        {loading ? (
          <Box
            sx={{
              minHeight: 640,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DayPilotMonth
            startDate={monthStart.format('YYYY-MM-DD')}
            events={visibleEvents}
            weekStarts={1}
            locale="ko-kr"
            eventMoveHandling="Disabled"
            eventResizeHandling="Disabled"
            onEventClick={(args) => {
              const tags = args?.e?.data?.tags;
              // 직접 등록한 일정은 그 자리에서 고친다.
              if (tags?.type === 'mySchedule') {
                setScheduleForm(tags.schedule);
                return;
              }
              // 기술지원은 기존 기술지원 상세(스테퍼) 팝업을 그대로 띄운다.
              if (tags?.type === 'tech') {
                if (tags.taskUuid) {
                  setTechReqId(tags.taskUuid);
                }
                return;
              }
              // 나머지(영업기회/프로젝트/발행예정/수금예정)는 원래 화면으로 보낸다.
              if (tags?.link) {
                navigate(tags.link);
              }
            }}
          />
        )}
      </Paper>

      <SalesScheduleFormDialog
        open={Boolean(scheduleForm)}
        schedule={scheduleForm?.schdUuid ? scheduleForm : null}
        currentUsrUuid={user?.usrUuid}
        onClose={() => setScheduleForm(null)}
        onSaved={() => {
          setScheduleForm(null);
          loadEvents();
        }}
      />

      {techReqId && (
        <TechEditStepperModalContainer
          open={Boolean(techReqId)}
          handleClose={() => setTechReqId(null)}
          reqId={techReqId}
        />
      )}
    </Box>
  );
};

export default SalesScheduleContainer;
